import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Group,
  Button,
  Table,
  Badge,
  Modal,
  NumberInput,
  Pagination,
  Loader,
  Center,
  Text,
  ThemeIcon,
  Card,
} from "@mantine/core";
import { IconPlus, IconClipboardCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function AuditList() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [audits, setAudits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [auditLimit, setAuditLimit] = useState<number>(1000);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.audit.list({ page, limit: 20 });
      setAudits(res.data);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.audit.create({ limit: auditLimit });
      notifications.show({ title: "Амжилттай", message: "Аудит үүсгэж эхэллээ", color: "blue" });
      setModalOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>Аудит</Title>
          <Badge variant="light" size="lg">{total}</Badge>
        </Group>
        {hasRole("admin", "maintainer") && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setModalOpen(true)} size="sm">
            Аудит үүсгэх
          </Button>
        )}
      </Group>

      {loading ? (
        <Center h={300}><Loader /></Center>
      ) : audits.length === 0 ? (
        <Center h={300}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} variant="light" color="gray" radius="xl">
              <IconClipboardCheck size={24} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">Аудит байхгүй</Text>
          </Stack>
        </Center>
      ) : (
        <Card shadow="xs" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={600}>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Зургийн тоо</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Буруу</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төлөв</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Огноо</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {audits.map((audit, idx) => (
                  <Table.Tr key={audit._id} onClick={() => navigate(`/audit/${audit._id}`)} style={{ cursor: "pointer" }}>
                    <Table.Td c="dimmed" fz="sm">{(page - 1) * 20 + idx + 1}</Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{audit.name}</Text></Table.Td>
                    <Table.Td><Text size="sm">{audit.imageCount}</Text></Table.Td>
                    <Table.Td><Badge color="red" variant="light" size="sm">{audit.invalidCount}</Badge></Table.Td>
                    <Table.Td>
                      <Badge color={audit.auditStatus === "done" ? "green" : "yellow"} size="sm">{audit.auditStatus}</Badge>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{formatDate(audit.createdAt)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalPages > 1 && (
            <Group justify="center" p="md" style={{ borderTop: '1px solid var(--app-border)' }}>
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          )}
        </Card>
      )}

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={<Text fw={600}>Аудит үүсгэх</Text>} size="sm" centered>
        <Stack>
          <NumberInput label="Зургийн тоо хязгаар" value={auditLimit} onChange={(v) => setAuditLimit(Number(v) || 1000)} min={1} max={5000} size="sm" />
          <Button onClick={handleCreate} loading={creating} size="sm">Үүсгэх</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
